import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiLock } from "react-icons/fi";
import { formatExactDate, formatRelativeTime } from "../Helper/dates";
import { fetchTaxonomies } from "../Redux/Slice/TaxonomiesSlice";
import type { IApplicationState } from "../Redux/Reducers";

/**
 * Read-only taxonomy list. Taxonomies are reference data defined in code (C# manifest) and seeded by
 * the ReferenceDataReconciler on startup : there is no create/edit/delete API or UI here on purpose.
 */
const TaxonomiePage: React.FC = () => {
	const dispatch = useDispatch();
	const { items: taxonomies, loading, error, isRefreshing, lastUpdated, initialized } = useSelector(
		(state: IApplicationState) => state.taxonomies
	);

	useEffect(() => {
		if (!initialized && !loading && !isRefreshing) {
			dispatch(fetchTaxonomies() as any);
		}
	}, [dispatch, initialized, isRefreshing, loading]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Taxonomie</p>
						<h1 className="text-2xl font-semibold text-slate-900">Taxonomie list</h1>
						{lastUpdated && (
							<p className="text-xs text-slate-500">
								Last sync: {formatExactDate(lastUpdated)} ({formatRelativeTime(lastUpdated)})
							</p>
						)}
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-3">
						<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
							<FiLock className="h-3 w-3" /> Géré dans le code
						</span>
						<div className="text-sm text-slate-500">{taxonomies.length} total</div>
						<button
							type="button"
							onClick={() => dispatch(fetchTaxonomies(true) as any)}
							disabled={isRefreshing}
							className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{isRefreshing ? "Refreshing…" : "Refresh"}
						</button>
					</div>
				</div>

				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
						<div>
							<p className="text-sm font-medium uppercase tracking-wide text-slate-500">Taxonomies</p>
							<p className="text-sm text-slate-500">
								Showing {taxonomies.length} taxonomie{taxonomies.length === 1 ? "" : "s"} · reference data, read-only
							</p>
						</div>
					</header>
					{error && (
						<div className="m-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
					)}
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
									<th className="px-5 py-3">Id</th>
									<th className="px-5 py-3">Title</th>
									<th className="px-5 py-3">Updated</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr>
										<td colSpan={3} className="px-5 py-6 text-center text-slate-500">
											Loading taxonomies…
										</td>
									</tr>
								) : taxonomies.length === 0 ? (
									<tr>
										<td colSpan={3} className="px-5 py-10 text-center text-slate-500">
											No taxonomies found.
										</td>
									</tr>
								) : (
									taxonomies.map((item, index) => (
										<tr key={item.id ?? item.title ?? index} className="hover:bg-slate-50">
											<td className="px-5 py-3 text-xs font-mono text-slate-600">{item.id ?? "-"}</td>
											<td className="px-5 py-3 text-slate-900">
												<div className="font-semibold">{item.title || "Untitled"}</div>
											</td>
											<td className="whitespace-nowrap px-5 py-3 text-slate-500">
												<div className="text-xs font-semibold text-slate-900">
													{item.updatedAt ? formatExactDate(item.updatedAt) : "-"}
												</div>
												<div className="text-[11px] uppercase tracking-wide text-slate-400">
													{item.updatedAt ? formatRelativeTime(item.updatedAt) : ""}
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</section>
			</div>
		</div>
	);
};

export default TaxonomiePage;
